/* ============================================================
   The hosted UI's /oauth2 token endpoints, as plain form POSTs. These are what
   expo-auth-session's exchangeCodeAsync / refreshAsync / revokeAsync do under
   the hood, written out here so every platform runs ONE implementation and the
   only platform-specific step left in social sign-in is opening the browser
   (adapters.ts → OAuthAdapter). Nothing here is authenticated beyond the public
   client id: the app client has no secret.
   ============================================================ */
import type { CognitoTokens } from './cognitoApi';

interface TokenResponse {
  access_token?: string;
  id_token?: string;
  refresh_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
}

async function postForm(url: string, fields: Record<string, string>): Promise<TokenResponse> {
  const body = new URLSearchParams(fields).toString();
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  const json = (await res.json().catch(() => ({}))) as TokenResponse;
  if (!res.ok || json.error) {
    throw new Error(json.error_description || json.error || `Token request failed (${res.status})`);
  }
  return json;
}

function toTokens(r: TokenResponse): CognitoTokens {
  if (!r.access_token || !r.id_token) throw new Error('Token response was incomplete.');
  return {
    accessToken: r.access_token,
    idToken: r.id_token,
    refreshToken: r.refresh_token ?? null,
    expiresAt: Date.now() + (r.expires_in ?? 3600) * 1000,
  };
}

/** Authorization code (+ PKCE verifier) → tokens. `redirectUri` must be the
   one the authorize request was built with. */
export async function exchangeCode(
  tokenEndpoint: string,
  params: { clientId: string; code: string; redirectUri: string; codeVerifier: string | null },
): Promise<CognitoTokens> {
  return toTokens(
    await postForm(tokenEndpoint, {
      grant_type: 'authorization_code',
      client_id: params.clientId,
      code: params.code,
      redirect_uri: params.redirectUri,
      ...(params.codeVerifier ? { code_verifier: params.codeVerifier } : {}),
    }),
  );
}

/** Refresh a hosted-UI session. Cognito omits the refresh token on refresh;
   the caller keeps the one it has. */
export async function refreshOAuthTokens(
  tokenEndpoint: string,
  params: { clientId: string; refreshToken: string },
): Promise<CognitoTokens> {
  return toTokens(
    await postForm(tokenEndpoint, {
      grant_type: 'refresh_token',
      client_id: params.clientId,
      refresh_token: params.refreshToken,
    }),
  );
}

/** Best-effort server-side revoke of a refresh token. The endpoint is the same
   for both sign-in origins — it's the pool's token, not the front door's. */
export async function revokeToken(
  revocationEndpoint: string,
  params: { clientId: string; token: string },
): Promise<void> {
  const body = new URLSearchParams({ token: params.token, client_id: params.clientId }).toString();
  const res = await fetch(revocationEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) throw new Error(`Revoke failed (${res.status})`);
}
